import { localeRowDirection } from '@/lib/rtl-layout';
import { getAndroidFlatSurfaceStyle } from '@/components/common/android-flat-surface';
import React, { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  ActionSheetIOS,
  Alert,
  View,
  Pressable,
  ScrollView,
  Platform,
  StyleSheet,
  Image,
  type LayoutChangeEvent,
} from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import { usePathname, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  Search,
  X,
  Home,
  User,
  Users,
  Building2,
  Settings,
  Check,
  Plus,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  Pin,
} from 'lucide-react-native';
import {
  AnalyticsIcon,
  AppsIcon,
  CvIcon,
  JobIcon,
  MatchIcon,
  MemoryGraphIcon,
  MessageIcon,
  NewChatIcon,
  NotificationIcon,
  SkillsHubIcon,
  SitesIcon,
  TasksIcon,
  ViewsIcon,
} from '../icons/sidebar-icons';
import { colors, shadows } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../hooks/useAuth';
import { useWorkspace } from '../../providers/useWorkspace';
import {
  useClearAllThreads,
  useDeleteThread,
  useThreads,
  useUpdateThread,
} from '../../hooks/useThreads';
import {
  filterThreadsByQuery,
  groupThreadsByRecency,
  threadDisplayTitle,
  threadGroupLabelKey,
} from '../../lib/thread-groups';
import type { AgentThreadSummary } from '@tabbio/types';
import { GlassView } from '@/components/common/GlassView';
import { useGlassAvailable } from '../../hooks/useGlassAvailable';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useNotificationUnreadCount } from '../../hooks/useNotificationUnreadCount';
import { useTour } from '../tour/useTour';
import {
  DRAWER_VISUAL_SPEC,
  estimateDrawerFooterBlockHeight,
  estimateDrawerHeaderBlockHeight,
  getDrawerVisualPalette,
} from './drawerVisualSpec';
import { useToast } from '../../hooks/useToast';
import { useLocale } from '../../hooks/useLocale';
import { getNavItemsForWorkspace, getWorkspaceThreadHref } from '../../lib/sidebar';
import { usePublishingCapability } from '../../hooks/useSites';
import { clearDrawerReturn, markPushedFromDrawer } from '../../lib/drawer-return';
import { getWorkspaceChatHref, getWorkspaceDefaultHref } from '../../lib/workspace-routes';
import { EMPLOYER_WORKSPACES_ENABLED } from '../../lib/launch-scope';
import { getChatRouteTransition, getActiveChatThreadId } from '../../lib/chat-navigation';
import type { TourTargetId } from '../tour/types';
import { fetchThreadMessages } from '../../lib/agent-threads';
import { QUERY_KEYS } from '../../lib/query-keys';

// The "tabbio" wordmark (228x54, dark ink on transparent). Tinted to the theme
// text colour so one asset serves light and dark without a second export.
const WORDMARK = require('../../../assets/branding/logo-main.png');
const DEFAULT_USER_AVATAR = require('../../../assets/default-avatar.png');
// Gap between the pinned header block and the first row in the scroll body.
const SCROLL_CONTENT_TOP_GAP = 4;
// Gap between the last row in the scroll body and the pinned footer.
const SCROLL_CONTENT_BOTTOM_GAP = 12;

type NavRowItem = {
  key: string;
  label: string;
  href?: string;
  icon: (props: { color: string; size: number }) => ReactNode;
  onPress: () => void;
  tourTargetId?: TourTargetId;
  /** Neutral count (e.g. CV views): text-coloured pill. */
  neutralCount?: number;
  /** Alert count (unread inbox/notifications on the employer drawer): red pill. */
  alertCount?: number;
  /** Coming-soon row: muted, non-interactive, trailing "Soon" label. */
  disabled?: boolean;
};

function GlassSurface({
  isGlass,
  fallbackBackground,
  style,
  children,
}: {
  isGlass: boolean;
  fallbackBackground: string;
  style: any;
  children: ReactNode;
}) {
  if (isGlass) {
    return (
      <GlassView glassEffectStyle="regular" isInteractive style={style}>
        {children}
      </GlassView>
    );
  }

  return (
    <View
      style={[
        { backgroundColor: fallbackBackground },
        style,
        // Android uses plain toolbar icons; translucent fills plus elevation
        // produce raised rings instead of the native iOS glass treatment.
        getAndroidFlatSurfaceStyle('transparent'),
      ]}
    >
      {children}
    </View>
  );
}

// A labeled, collapsible group inside the scrollable drawer body (Chats).
// Mirrors the web sidebar's `.tabbio-section` / `.tabbio-section-header`
// collapsible pattern so both surfaces share the same behaviour. Defaults to
// expanded, matching the reference design where every group starts open.
function CollapsibleSection({
  title,
  trailingAction,
  textColor,
  pressedBg,
  fontFamily,
  defaultExpanded = true,
  children,
}: {
  title: string;
  trailingAction?: ReactNode;
  textColor: string;
  pressedBg: string;
  fontFamily: string | undefined;
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <View style={styles.categorySection}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        onPress={() => setExpanded((prev) => !prev)}
        style={({ pressed }) => [
          styles.categoryHeader,
          pressed && { backgroundColor: pressedBg },
        ]}
      >
        <Text
          style={[styles.categoryTitle, { color: textColor, fontFamily }]}
          numberOfLines={1}
        >
          {title}
        </Text>
        {trailingAction}
        {expanded ? (
          <ChevronDown size={16} color={textColor} strokeWidth={2.2} />
        ) : (
          <ChevronRight size={16} color={textColor} strokeWidth={2.2} />
        )}
      </Pressable>
      {expanded ? <View style={styles.categoryBody}>{children}</View> : null}
    </View>
  );
}

export function DrawerContent({ navigation }: { navigation: any }) {
  const { isDark, theme } = useTheme();
  const { fonts } = useFont();
  const palette = getDrawerVisualPalette(isDark);
  const { t } = useTranslation(['common', 'seeker', 'employer']);
  const haptics = useHaptics();
  const router = useRouter();
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const toast = useToast();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { workspaces, activeWorkspace, switchWorkspace, mode, companyId } = useWorkspace();
  const isGlass = useGlassAvailable();

  const { threads, refetch: refetchThreads } = useThreads();
  const deleteThreadMutation = useDeleteThread();
  const clearAllThreadsMutation = useClearAllThreads();
  const updateThreadMutation = useUpdateThread();
  const { isRTL } = useLocale();
  // Unread counts only surface here on the employer drawer, whose nav still
  // lists Inbox and Notifications (the personal workspace shows them on the
  // My Tabbio hub). Both queries are app-wide caches fed by the socket, so
  // reading them costs nothing on the personal drawer.
  const { count: messageUnreadCount } = useUnreadCount();
  const { count: notificationUnreadCount } = useNotificationUnreadCount();
  const [searchQuery, setSearchQuery] = useState('');
  const [showWorkspacePopout, setShowWorkspacePopout] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const tour = useTour();
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const chatRevealRafRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);
  const activeThreadId = getActiveChatThreadId(pathname);
  const threadCopyPrefix = activeWorkspace.type === 'company' ? 'employer:chat' : 'seeker:chat';
  const scopedAgentCompanyId = mode === 'employer' ? companyId : undefined;

  const topPadding = Platform.OS === 'ios'
    ? DRAWER_VISUAL_SPEC.topPaddingIOS
    : DRAWER_VISUAL_SPEC.topPaddingAndroid;

  // The header and footer blocks are overlaid on the scroll body, so their
  // heights have to be paid back as content padding for the list to start
  // below the header and end above the footer.
  const [headerBlockHeight, setHeaderBlockHeight] = useState(() =>
    estimateDrawerHeaderBlockHeight(topPadding),
  );
  const [footerBlockHeight, setFooterBlockHeight] = useState(() =>
    estimateDrawerFooterBlockHeight(insets.bottom),
  );

  const handleHeaderBlockLayout = (event: LayoutChangeEvent) => {
    const measured = Math.round(event.nativeEvent.layout.height);
    setHeaderBlockHeight((current) => (current === measured ? current : measured));
  };

  const handleFooterBlockLayout = (event: LayoutChangeEvent) => {
    const measured = Math.round(event.nativeEvent.layout.height);
    setFooterBlockHeight((current) => (current === measured ? current : measured));
  };

  // Issue #285 telemetry. The scroll body is supposed to sit at the drawer
  // origin now, so this is quiet by design - it only fires if the native
  // layout puts it somewhere else, which is the measurement we never had when
  // the overlapping-menu reports first came in.
  const reportUnexpectedScrollFrame = (event: LayoutChangeEvent) => {
    const { x, y } = event.nativeEvent.layout;
    if (Math.abs(x) > 1 || Math.abs(y) > 1) {
      console.warn('[drawer] scroll body laid out away from the drawer origin', { x, y });
    }
  };

  useEffect(() => {
    const unsub = navigation.addListener?.('drawerOpen', () => {
      refetchThreads();
      tour.notifyDrawerOpened();
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [navigation, refetchThreads, tour]);

  useEffect(
    () => () => {
      if (chatRevealRafRef.current != null) {
        cancelAnimationFrame(chatRevealRafRef.current);
      }
    },
    [],
  );

  const sortedThreads = useMemo(
    () =>
      [...threads].sort((a, b) => {
        const aTs = new Date(a.lastMessageAt || a.createdAt).getTime();
        const bTs = new Date(b.lastMessageAt || b.createdAt).getTime();
        return bTs - aTs;
      }),
    [threads],
  );

  const filteredThreads = useMemo(
    () => filterThreadsByQuery(sortedThreads, searchQuery),
    [searchQuery, sortedThreads],
  );

  // Grouped by recency so "yesterday" and "four months ago" stop sitting
  // against each other with nothing to tell them apart. Shared with the web
  // sidebar via lib/thread-groups.
  const threadGroups = useMemo(
    () => groupThreadsByRecency(filteredThreads),
    [filteredThreads],
  );

  const closeAllOverlays = () => {
    setShowWorkspacePopout(false);
  };

  const closeDrawerThen = (action: () => void) => {
    closeAllOverlays();
    navigation.closeDrawer();
    action();
  };

  // Navigate FIRST, then close the drawer a frame or two later. The target
  // begins rendering behind the still-open drawer, so it covers the previous
  // screen before the drawer slides away: users never see the chat/home screen
  // flash in the middle of the transition. Mirrors `navigateChatRoute` (#113);
  // the close-drawer-first ordering was what exposed that flash. (Issue #128)
  const navigateThenCloseDrawer = (navigate: () => void) => {
    closeAllOverlays();
    if (chatRevealRafRef.current != null) {
      cancelAnimationFrame(chatRevealRafRef.current);
      chatRevealRafRef.current = null;
    }

    navigate();

    chatRevealRafRef.current = requestAnimationFrame(() => {
      chatRevealRafRef.current = requestAnimationFrame(() => {
        chatRevealRafRef.current = null;
        navigation.closeDrawer();
      });
    });
  };

  // Destinations that live outside the drawer navigator get pushed onto the
  // root stack, so backing out of them would otherwise reveal whichever drawer
  // screen was mounted underneath instead of the drawer the user came from.
  // Marking the push lets the root stack re-open the drawer on dismissal.
  const pushRoute = (href: string) => {
    haptics.trigger('light');
    markPushedFromDrawer(href);
    navigateThenCloseDrawer(() => router.push(href as any));
  };

  // Settings is a root-stack push like Inbox, Notifications, Automation and
  // Memory, and takes the same ordering: push, then close the drawer. It used
  // to be the one exception (close the drawer, wait, then push) on the
  // grounds that its iOS form sheet needed the drawer gone first. Waiting is
  // what broke it: from a busy screen (Jobs, Skills) the push landed in a
  // state where UIKit never performed the presentation, so the drawer shut,
  // nothing happened, and the sheet only appeared on the next drawer
  // interaction. `/settings/memory` is the proof: same route group, same
  // form-sheet presentation, pushed this way, and it has always worked.
  const openSettingsRoute = () => {
    haptics.trigger('light');
    markPushedFromDrawer('/settings');
    navigateThenCloseDrawer(() => router.push('/settings' as any));
  };

  // Switching between sibling drawer destinations must use `navigate`, not
  // `push`. `navigate` unwinds to the existing route instead of stacking a
  // second copy of the shell (which made CV feel like "two screens" and
  // left back gestures jumping to chat with no transition).
  const navigateRoute = (href: string) => {
    haptics.trigger('light');
    // Staying inside the drawer navigator: any earlier off-drawer mark is stale
    // now, and must not pop the drawer open on some later, unrelated dismissal.
    clearDrawerReturn();
    navigateThenCloseDrawer(() => router.navigate(href as any));
  };

  // Issue #162: "New chat" must ALWAYS produce a fresh session. After the first
  // message the chat index keeps the conversation in memory while its route
  // stays `/chat`, so a plain navigate() is a no-op and the stale conversation
  // stays on screen until the app is force-closed. Replacing with a unique
  // `fresh` param remounts the chat index's provider (it keys on the param),
  // resetting the session deterministically from anywhere in the app.
  const startNewChat = () => {
    haptics.trigger('light');
    clearDrawerReturn();
    navigateThenCloseDrawer(() =>
      router.replace({
        pathname: getWorkspaceChatHref(activeWorkspace),
        params: { fresh: Date.now().toString(36) },
      } as any),
    );
  };

  const replaceChatRoute = (href: string) => {
    const transition = getChatRouteTransition(pathname, href, {
      replaceFromAnyRoute: true,
    });

    if (transition === 'none') {
      return transition;
    }

    if (transition === 'replace') {
      router.replace(href as any);
      return transition;
    }

    router.push(href as any);
    return transition;
  };

  // A drawer chat switch must feel instant: the drawer always starts closing on
  // tap, never waiting on the target to load (that made it feel stuck). But we
  // navigate FIRST and yield a frame or two before closing, so the target begins
  // rendering behind the still-open drawer: a warm thread commits its content in
  // that gap and the slide reveals it already in place (smooth), while a cold
  // thread just reveals its loading state and streams in behind. The ~1-2 frame
  // yield is imperceptible, so the tap still feels immediate. Combined with the
  // chat Stack's `animation: 'none'`, there's no inner slide. (#113)
  const navigateChatRoute = (href: string, feedback: 'light' | 'selection' = 'light') => {
    haptics.trigger(feedback);
    clearDrawerReturn();
    closeAllOverlays();
    if (chatRevealRafRef.current != null) {
      cancelAnimationFrame(chatRevealRafRef.current);
      chatRevealRafRef.current = null;
    }

    const transition = replaceChatRoute(href);
    if (transition === 'none') {
      // Already on the target: nothing to reveal, just close.
      navigation.closeDrawer();
      return;
    }

    chatRevealRafRef.current = requestAnimationFrame(() => {
      chatRevealRafRef.current = requestAnimationFrame(() => {
        chatRevealRafRef.current = null;
        navigation.closeDrawer();
      });
    });
  };

  const warmThreadMessages = (threadId: string) => {
    const queryKey = QUERY_KEYS.THREAD_MESSAGES(threadId, mode, scopedAgentCompanyId);
    const cached = queryClient.getQueryData<{ messages: any[]; toolCalls: any[] }>(queryKey);

    if (cached && (cached.messages.length > 0 || cached.toolCalls.length > 0)) {
      return;
    }

    queryClient.removeQueries({ queryKey, exact: true });
    queryClient
      .prefetchQuery({
        queryKey,
        queryFn: () => fetchThreadMessages(threadId, mode, scopedAgentCompanyId),
        staleTime: 2 * 60 * 1000,
      })
      .catch((error) => {
        console.error('[chat] thread history prefetch failed', error);
      });
  };

  const deleteThreadWithFeedback = (thread: { id: string; title?: string | null }) => {
    setDeletingThreadId(thread.id);
    deleteThreadMutation.mutate(thread.id, {
      onSuccess: () => {
        toast.success(t(`${threadCopyPrefix}.deleteThreadSuccess`));
        if (activeThreadId === thread.id) {
          router.replace(getWorkspaceChatHref(activeWorkspace) as any);
        }
      },
      onError: () => {
        toast.error(
          t(`${threadCopyPrefix}.deleteThreadFailed`),
          t(`${threadCopyPrefix}.deleteThreadFailedMessage`),
        );
      },
      onSettled: () => {
        setDeletingThreadId((current) => (current === thread.id ? null : current));
      },
    });
  };

  const confirmClearAllHistory = () => {
    if (clearAllThreadsMutation.isPending || sortedThreads.length === 0) return;

    haptics.trigger('medium');
    Alert.alert(
      t(`${threadCopyPrefix}.clearAllHistoryTitle`),
      t(`${threadCopyPrefix}.clearAllHistoryMessage`),
      [
        { text: t('actions.cancel'), style: 'cancel' },
        {
          text: t(`${threadCopyPrefix}.clearAllHistoryConfirm`),
          style: 'destructive',
          onPress: () => {
            const snapshot = sortedThreads.slice();
            const activeId = activeThreadId;
            clearAllThreadsMutation.mutate(snapshot, {
              onSuccess: ({ deleted, failed }) => {
                if (failed > 0 && deleted === 0) {
                  toast.error(t(`${threadCopyPrefix}.clearAllHistoryFailed`));
                } else if (failed > 0) {
                  toast.error(t(`${threadCopyPrefix}.clearAllHistoryPartial`));
                } else {
                  toast.success(t(`${threadCopyPrefix}.clearAllHistorySuccess`));
                }
                if (activeId && snapshot.some((thread) => thread.id === activeId)) {
                  router.replace(getWorkspaceChatHref(activeWorkspace) as any);
                }
              },
              onError: () => {
                toast.error(t(`${threadCopyPrefix}.clearAllHistoryFailed`));
              },
            });
          },
        },
      ],
      { cancelable: true },
    );
  };

  const openHistoryActions = () => {
    if (clearAllThreadsMutation.isPending || sortedThreads.length === 0) return;
    haptics.trigger('light');

    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: [
            t(`${threadCopyPrefix}.clearAllHistoryAction`),
            t('actions.cancel'),
          ],
          destructiveButtonIndex: 0,
          cancelButtonIndex: 1,
        },
        (index) => {
          if (index === 0) confirmClearAllHistory();
        },
      );
      return;
    }

    Alert.alert(t('chat.recents'), undefined, [
      {
        text: t(`${threadCopyPrefix}.clearAllHistoryAction`),
        style: 'destructive',
        onPress: confirmClearAllHistory,
      },
      { text: t('actions.cancel'), style: 'cancel' },
    ]);
  };

  const confirmDeleteThread = (thread: { id: string; title?: string | null }) => {
    if (deletingThreadId) return;

    haptics.trigger('medium');
    const title = thread.title || t(`${threadCopyPrefix}.untitledThread`);
    Alert.alert(
      t(`${threadCopyPrefix}.deleteThreadTitle`),
      t(`${threadCopyPrefix}.deleteThreadMessage`, { title }),
      [
        {
          text: t('actions.cancel'),
          style: 'cancel',
        },
        {
          text: t(`${threadCopyPrefix}.deleteThreadConfirm`),
          style: 'destructive',
          onPress: () => deleteThreadWithFeedback(thread),
        },
      ],
      { cancelable: true },
    );
  };

  /**
   * Rename happens inline in the row, not in a prompt dialog.
   *
   * `Alert.prompt` is iOS-only, so a dialog would leave rename silently dead on
   * Android. Editing in place also matches the web sidebar, and keeps the chat
   * the user is renaming visible next to the field.
   */
  const commitRename = (thread: AgentThreadSummary, value: string) => {
    const title = value.trim();
    setRenamingThreadId(null);
    if (!title || title === thread.title) return;

    updateThreadMutation.mutate(
      { threadId: thread.id, patch: { title } },
      { onError: () => toast.error(t('chat.threadActions.renameFailed')) },
    );
  };

  const startRename = (thread: AgentThreadSummary) => {
    setRenameDraft(thread.title ?? '');
    setRenamingThreadId(thread.id);
  };

  /**
   * Long-press opens the row's actions rather than deleting outright.
   *
   * Delete used to be the only long-press outcome, which made the single
   * gesture available on a chat row also the destructive one. Rename and pin
   * now live behind the same gesture, with delete still marked destructive.
   */
  const openThreadActions = (thread: AgentThreadSummary) => {
    if (deletingThreadId) return;
    haptics.trigger('medium');

    const pinLabel = thread.pinned
      ? t('chat.threadActions.unpin')
      : t('chat.threadActions.pin');

    const run = (index: number) => {
      if (index === 0) startRename(thread);
      else if (index === 1) {
        updateThreadMutation.mutate(
          { threadId: thread.id, patch: { pinned: !thread.pinned } },
          { onError: () => toast.error(t('chat.threadActions.pinFailed')) },
        );
      } else if (index === 2) confirmDeleteThread(thread);
    };

    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          title: thread.title ?? t(`${threadCopyPrefix}.untitledThread`),
          options: [
            t('chat.threadActions.rename'),
            pinLabel,
            t('chat.threadActions.delete'),
            t('actions.cancel'),
          ],
          destructiveButtonIndex: 2,
          cancelButtonIndex: 3,
        },
        (index) => run(index),
      );
      return;
    }

    Alert.alert(thread.title ?? t(`${threadCopyPrefix}.untitledThread`), undefined, [
      { text: t('chat.threadActions.rename'), onPress: () => run(0) },
      { text: pinLabel, onPress: () => run(1) },
      { text: t('chat.threadActions.delete'), style: 'destructive', onPress: () => run(2) },
      { text: t('actions.cancel'), style: 'cancel' },
    ]);
  };

  // The footer gear. Seeker-only launch (EMPLOYER_WORKSPACES_ENABLED false):
  // straight to Settings. With workspaces on, it opens the account popout
  // (switch/add workspace + Settings) so the switcher stays reachable now that
  // the header no longer carries the workspace avatar.
  const handleSettingsPress = () => {
    if (!EMPLOYER_WORKSPACES_ENABLED) {
      openSettingsRoute();
      return;
    }

    haptics.trigger('light');
    setShowWorkspacePopout((prev) => !prev);
  };

  const toggleSearch = () => {
    haptics.trigger('light');
    setShowSearch((prev) => {
      if (prev) setSearchQuery('');
      return !prev;
    });
  };

  // Same icons as the web sidebar's NAV_ICON_COMPONENTS (sidebar-icons set).
  // Home/User/Users keep lucide equivalents: the web renders those via the
  // Material Symbols font, which is not loaded natively.
  const toolIconMap: Record<string, (props: { color: string; size: number }) => ReactNode> = {
    Home: ({ color, size }) => <Home size={size} color={color} strokeWidth={2} />,
    User: ({ color, size }) => <User size={size} color={color} strokeWidth={2} />,
    FileText: ({ color, size }) => <CvIcon size={size} color={color} />,
    Skills: ({ color, size }) => <SkillsHubIcon size={size} color={color} />,
    Globe: ({ color, size }) => <SitesIcon size={size} color={color} />,
    Briefcase: ({ color, size }) => <JobIcon size={size} color={color} />,
    Bell: ({ color, size }) => <NotificationIcon size={size} color={color} />,
    MessageCircle: ({ color, size }) => <MessageIcon size={size} color={color} />,
    Grid3X3: ({ color, size }) => <AppsIcon size={size} color={color} />,
    PlugZap: ({ color, size }) => <AppsIcon size={size} color={color} />,
    BarChart3: ({ color, size }) => <AnalyticsIcon size={size} color={color} />,
    Eye: ({ color, size }) => <ViewsIcon size={size} color={color} />,
    Users: ({ color, size }) => <Users size={size} color={color} strokeWidth={2} />,
    Zap: ({ color, size }) => <MatchIcon size={size} color={color} />,
    // "Tasks" is the Automation board (Nova): same href, new label and glyph.
    Workflow: ({ color, size }) => <TasksIcon size={size} color={color} />,
    GitGraph: ({ color, size }) => <MemoryGraphIcon size={size} color={color} />,
  };

  // Keys must be hrefs that personalNav actually renders (see sidebar.ts) -
  // a stale href leaves its DRAWER_CAPTIONS stop pointing at nothing.
  const toolTourTargetByHref: Partial<Record<string, TourTargetId>> = {
    '/(app)/chat': 'drawer.chat',
    '/(app)/cv/my-tabbio': 'drawer.webcv',
    '/(app)/jobs': 'drawer.jobs',
    '/auto-apply': 'drawer.automatch',
    '/(app)/skills': 'drawer.skills',
  };

  // Sites (plan 7.10) joins after Skills where publishing is available.
  const publishing = usePublishingCapability();
  const workspaceChatHref = getWorkspaceChatHref(activeWorkspace);

  // One flat list in data order. Chat is the footer's "Chat" pill, so its row
  // is filtered out. Personal workspaces no longer list Inbox/Notifications
  // (they live on the My Tabbio hub); the employer nav still does, and those
  // render as ordinary rows with their unread counts.
  const navRows = getNavItemsForWorkspace(t, activeWorkspace.type, {
    sites: Boolean(publishing?.available),
  })
    .filter((item) => (item.href || item.disabled) && item.href !== workspaceChatHref)
    .map((item): NavRowItem => ({
      key: item.label,
      label: item.label,
      href: item.href ?? undefined,
      icon: toolIconMap[item.iconName] || toolIconMap.User,
      disabled: item.disabled,
      // Drawer destinations switch screens (navigate); only routes that live
      // outside the drawer (auto-apply, messages, …) get pushed onto the stack.
      onPress: () => {
        if (item.disabled || !item.href) {
          return;
        }
        if (item.type === 'push') {
          pushRoute(item.href);
        } else {
          navigateRoute(item.href);
        }
      },
      tourTargetId: item.href ? toolTourTargetByHref[item.href] : undefined,
      alertCount:
        item.badgeKey === 'messages'
          ? messageUnreadCount
          : item.badgeKey === 'notifications'
            ? notificationUnreadCount
            : undefined,
    }));

  const renderNavRow = (row: NavRowItem) => {
    if (row.disabled) {
      // Coming-soon row: non-interactive, muted, trailing "Soon".
      return (
        <View key={row.key} style={[styles.navRow, isRTL && { flexDirection: 'row-reverse' }]} accessibilityState={{ disabled: true }}>
          <View style={styles.navRowIcon}>
            {row.icon({ color: theme.textTertiary, size: DRAWER_VISUAL_SPEC.navIconSize })}
          </View>
          <Text
            style={[styles.navRowLabel, { color: theme.textTertiary, fontFamily: fonts.medium, textAlign: isRTL ? 'right' : 'left' }]}
            numberOfLines={1}
          >
            {row.label}
          </Text>
          <Text style={[styles.navRowSoon, { color: theme.textTertiary, fontFamily: fonts.medium }]}>
            {t('nav.soon')}
          </Text>
        </View>
      );
    }

    return (
      <Pressable
        key={row.key}
        ref={row.tourTargetId ? (ref) => tour.registerTargetRef(row.tourTargetId!, ref) : undefined}
        onPress={row.onPress}
        accessibilityRole="button"
        accessibilityLabel={row.label}
        style={({ pressed }) => [styles.navRow, isRTL && { flexDirection: 'row-reverse' }, pressed && { backgroundColor: palette.listPressedBg }]}
      >
        <View style={styles.navRowIcon}>
          {row.icon({ color: theme.text, size: DRAWER_VISUAL_SPEC.navIconSize })}
        </View>
        <Text
          style={[styles.navRowLabel, { color: theme.text, fontFamily: fonts.medium }]}
          numberOfLines={1}
        >
          {row.label}
        </Text>
        {(row.alertCount ?? 0) > 0 ? (
          <CountBadge count={row.alertCount ?? 0} />
        ) : (row.neutralCount ?? 0) > 0 ? (
          <View style={[styles.neutralCountBadge, { backgroundColor: theme.text }]}>
            <Text style={[styles.neutralCountBadgeText, { color: theme.background, fontFamily: fonts.bold }]}>
              {(row.neutralCount ?? 0) > 99 ? '99+' : row.neutralCount}
            </Text>
          </View>
        ) : null}
      </Pressable>
    );
  };

  // The scroll body deliberately fills the whole drawer from its origin, with
  // the header and footer blocks overlaid on top and their measured heights
  // paid back as content padding. Issue #285: the native renderer intermittently
  // laid this ScrollView out at the drawer origin at full size instead of below
  // the header, so the list painted across the header and into the status bar.
  // Making the origin its *correct* frame means that misplacement can no longer
  // change what is drawn.
  const scrollBody = (
    <ScrollView
      style={styles.scrollArea}
      contentContainerStyle={[
        styles.scrollContent,
        {
          paddingTop: headerBlockHeight + SCROLL_CONTENT_TOP_GAP,
          paddingBottom: footerBlockHeight + SCROLL_CONTENT_BOTTOM_GAP,
        },
      ]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      onLayout={reportUnexpectedScrollFrame}
    >
      {navRows.map(renderNavRow)}

      {/* No Connectors or Feedback rows here: both moved under Settings
          (2026-09), Feedback as the "Support" row. */}

      <CollapsibleSection
        title={t('chat.recents')}
        textColor={theme.text}
        pressedBg={palette.listPressedBg}
        fontFamily={fonts.semibold}
        trailingAction={
          sortedThreads.length > 0 ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(`${threadCopyPrefix}.clearAllHistoryAction`)}
              hitSlop={10}
              onPress={openHistoryActions}
              disabled={clearAllThreadsMutation.isPending}
              style={({ pressed }) => [
                styles.categoryHeaderAction,
                pressed && { backgroundColor: palette.listPressedBg },
                clearAllThreadsMutation.isPending && { opacity: 0.5 },
              ]}
            >
              <MoreHorizontal size={18} color={theme.textTertiary} strokeWidth={2.2} />
            </Pressable>
          ) : null
        }
      >
        {threadGroups.map((group) => (
          <React.Fragment key={group.key}>
            <Text
              style={[
                styles.threadGroupHeading,
                { color: palette.textSecondary, fontFamily: fonts.medium },
              ]}
            >
              {t(threadGroupLabelKey(group.key))}
            </Text>
            {group.threads.map((thread) =>
              renamingThreadId === thread.id ? (
                <View
                  key={thread.id}
                  style={[
                    styles.threadItem,
                    { flexDirection: localeRowDirection(isRTL) },
                  ]}
                >
                  <TextInput
                    value={renameDraft}
                    onChangeText={setRenameDraft}
                    autoFocus
                    returnKeyType="done"
                    selectTextOnFocus
                    placeholder={t('chat.threadActions.renamePlaceholder')}
                    placeholderTextColor={palette.textSecondary}
                    accessibilityLabel={t('chat.threadActions.renameTitle')}
                    onSubmitEditing={() => commitRename(thread, renameDraft)}
                    onBlur={() => commitRename(thread, renameDraft)}
                    style={[
                      styles.threadTitle,
                      { color: theme.text, fontFamily: fonts.medium },
                    ]}
                  />
                </View>
              ) : (
                <Pressable
                  key={thread.id}
                  onPress={() => {
                    warmThreadMessages(thread.id);
                    navigateChatRoute(
                      getWorkspaceThreadHref(activeWorkspace, thread.id),
                      'selection',
                    );
                  }}
                  onLongPress={() => openThreadActions(thread)}
                  delayLongPress={350}
                  disabled={deletingThreadId === thread.id}
                  accessibilityHint={t('chat.threadActions.more')}
                  style={({ pressed }) => [
                    styles.threadItem,
                    { flexDirection: localeRowDirection(isRTL) },
                    pressed && { backgroundColor: palette.listPressedBg },
                    deletingThreadId === thread.id && styles.threadItemDeleting,
                  ]}
                >
                  <Text
                    style={[styles.threadTitle, { color: theme.text, fontFamily: fonts.medium }]}
                    numberOfLines={1}
                  >
                    {threadDisplayTitle(thread, t('chat.newChat'))}
                  </Text>
                  {thread.pinned ? (
                    <Pin size={13} color={palette.textSecondary} strokeWidth={2} />
                  ) : null}
                </Pressable>
              ),
            )}
          </React.Fragment>
        ))}

        {filteredThreads.length === 0 && (
          <Text style={[styles.emptyText, { color: palette.textSecondary, fontFamily: fonts.regular }]}>
            {searchQuery.trim()
              ? t('chat.threadSearch.noResults', { query: searchQuery.trim() })
              : t('labels.noConversations')}
          </Text>
        )}
      </CollapsibleSection>
    </ScrollView>
  );

  return (
    <View style={[styles.drawer, { backgroundColor: palette.background }]}>
      {showWorkspacePopout && EMPLOYER_WORKSPACES_ENABLED && (
        <Pressable
          style={styles.popoutBackdrop}
          onPress={() => setShowWorkspacePopout(false)}
        />
      )}

      {scrollBody}

      {/* Pinned on top of the scroll body - rendered after it so it paints
          above, letting the rows/chats list scroll underneath. */}
      <View
        style={[styles.headerBlock, { paddingTop: topPadding, backgroundColor: palette.background }]}
        onLayout={handleHeaderBlockLayout}
      >
        <View style={styles.headerRow}>
          <Pressable
            accessibilityRole="link"
            accessibilityLabel={t('nav.home', { defaultValue: 'Home' })}
            onPress={() => navigateRoute(getWorkspaceDefaultHref(activeWorkspace))}
            hitSlop={8}
            style={styles.wordmarkPress}
          >
            <Image
              source={WORDMARK}
              style={[styles.wordmark, { tintColor: theme.text }]}
              resizeMode="contain"
            />
          </Pressable>

          <GlassSurface
            isGlass={isGlass}
            fallbackBackground={palette.headerSurfaceFallback}
            style={[
              styles.headerCircle,
              !isGlass && { borderWidth: StyleSheet.hairlineWidth, borderColor: palette.quickActionSurfaceBorder },
              styles.surfaceShadow,
            ]}
          >
            <Pressable
              onPress={toggleSearch}
              accessibilityRole="button"
              accessibilityLabel={t('nav.search')}
              accessibilityState={{ expanded: showSearch }}
              style={({ pressed }) => [
                styles.headerCirclePress,
                pressed && { backgroundColor: palette.quickActionPressedBg },
              ]}
            >
              {showSearch ? (
                <X size={20} color={theme.text} strokeWidth={2.2} />
              ) : (
                <Search size={20} color={theme.text} strokeWidth={2.1} />
              )}
            </Pressable>
          </GlassSurface>
        </View>

        {showSearch && (
          <View style={[styles.searchInlineRow, { backgroundColor: palette.utilitySurface }]}>
            <Search size={17} color={palette.textSecondary} strokeWidth={1.95} />
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder={t('chat.searchPlaceholder')}
              placeholderTextColor={palette.textSecondary}
              style={[styles.searchInput, { color: theme.text, fontFamily: fonts.regular }]}
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
              returnKeyType="search"
            />
            {searchQuery.length > 0 ? (
              <Pressable
                onPress={() => {
                  haptics.trigger('light');
                  setSearchQuery('');
                }}
                accessibilityRole="button"
                accessibilityLabel={t('actions.cancel')}
                style={styles.searchClose}
              >
                <X size={15} color={palette.textSecondary} strokeWidth={2.4} />
              </Pressable>
            ) : null}
          </View>
        )}
      </View>

      {/* Pinned footer: the purple Chat pill (new chat) and the settings gear.
          Overlaid like the header, with its measured height paid back as the
          scroll body's bottom padding. */}
      <View
        style={[
          styles.footerBlock,
          {
            paddingBottom: Math.max(insets.bottom, DRAWER_VISUAL_SPEC.footerPaddingBottom),
            backgroundColor: palette.background,
          },
        ]}
        onLayout={handleFooterBlockLayout}
      >
        <Pressable
          ref={(ref) => tour.registerTargetRef('drawer.chat', ref)}
          onPress={startNewChat}
          accessibilityRole="button"
          accessibilityLabel={t('chat.newChat')}
          style={({ pressed }) => [
            styles.chatPill,
            { backgroundColor: colors.primary, opacity: pressed ? 0.85 : 1 },
          ]}
        >
          <NewChatIcon size={DRAWER_VISUAL_SPEC.chatFabIconSize} color="#FFFFFF" />
          <Text style={[styles.chatPillLabel, { fontFamily: fonts.semibold }]} numberOfLines={1}>
            {t('drawer.chatCta')}
          </Text>
        </Pressable>

        <GlassSurface
          isGlass={isGlass}
          fallbackBackground={palette.headerSurfaceFallback}
          style={[
            styles.footerCircle,
            EMPLOYER_WORKSPACES_ENABLED && styles.footerMenu,
            !isGlass && { borderWidth: StyleSheet.hairlineWidth, borderColor: palette.quickActionSurfaceBorder },
            styles.surfaceShadow,
          ]}
        >
          <Pressable
            ref={(ref) => tour.registerTargetRef('drawer.settings', ref)}
            onPress={handleSettingsPress}
            accessibilityRole="button"
            accessibilityLabel={EMPLOYER_WORKSPACES_ENABLED
              ? `${t('settings.title')}, ${t('labels.switchWorkspace')}`
              : t('settings.title')}
            accessibilityState={EMPLOYER_WORKSPACES_ENABLED ? { expanded: showWorkspacePopout } : undefined}
            style={({ pressed }) => [
              styles.footerCirclePress,
              EMPLOYER_WORKSPACES_ENABLED && styles.footerMenu,
              pressed && { backgroundColor: palette.quickActionPressedBg },
            ]}
          >
            <Settings size={22} color={theme.text} strokeWidth={2} />
            {EMPLOYER_WORKSPACES_ENABLED && (
              <ChevronDown
                size={14}
                color={theme.textSecondary}
                strokeWidth={2}
                style={showWorkspacePopout ? { transform: [{ rotate: '180deg' }] } : undefined}
              />
            )}
          </Pressable>
        </GlassSurface>
      </View>

      {showWorkspacePopout && EMPLOYER_WORKSPACES_ENABLED && (
        <View
          style={[
            styles.popout,
            {
              backgroundColor: palette.popoutSurface,
              borderColor: palette.popoutBorder,
              bottom: footerBlockHeight + 8,
            },
          ]}
        >
          <Text style={[styles.popoutTitle, { color: palette.textSecondary, fontFamily: fonts.bold }]}>
            {t('labels.switchWorkspace')}
          </Text>
          {workspaces.map((ws) => {
            const isActive = ws.id === activeWorkspace.id;
            const label = ws.label;

            return (
              <Pressable
                key={ws.id}
                onPress={() => {
                  haptics.trigger('selection');
                  setShowWorkspacePopout(false);
                  clearDrawerReturn();
                  closeDrawerThen(() => {
                    void switchWorkspace(ws.id)
                      .then((workspace) => {
                        router.replace(getWorkspaceDefaultHref(workspace) as any);
                      })
                      .catch((error) => {
                        toast.error(
                          t('errors.generic'),
                          error instanceof Error ? error.message : t('errors.generic'),
                        );
                      });
                  });
                }}
                style={({ pressed }) => [
                  styles.popoutItem,
                  pressed && { backgroundColor: palette.listPressedBg },
                ]}
              >
                {ws.type === 'personal' ? (
                  <Image
                    source={user?.image ? { uri: user.image } : DEFAULT_USER_AVATAR}
                    style={styles.popoutBadge}
                    resizeMode="cover"
                  />
                ) : ws.logo ? (
                  <Image
                    source={{ uri: ws.logo }}
                    style={[styles.popoutBadge, { backgroundColor: palette.utilitySurface }]}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={[styles.popoutBadge, styles.popoutBadgeIcon, { backgroundColor: palette.utilitySurface }]}>
                    <Building2 size={14} color={theme.textTertiary} strokeWidth={1.8} />
                  </View>
                )}
                <Text style={[styles.popoutLabel, { color: theme.text, fontFamily: fonts.medium }]} numberOfLines={1}>
                  {label}
                </Text>
                {isActive ? <Check size={16} color={palette.upgradeAccent} strokeWidth={2.4} /> : null}
              </Pressable>
            );
          })}

          <Pressable
            onPress={() => {
              haptics.trigger('light');
              setShowWorkspacePopout(false);
              markPushedFromDrawer('/create-company');
              navigateThenCloseDrawer(() => router.push('/create-company' as any));
            }}
            style={({ pressed }) => [
              styles.popoutItem,
              pressed && { backgroundColor: palette.listPressedBg },
            ]}
          >
            <View style={[styles.popoutBadge, { backgroundColor: palette.utilitySurface }]}>
              <Plus size={14} color={palette.textSecondary} strokeWidth={2.3} />
            </View>
            <Text style={[styles.popoutLabel, { color: palette.textSecondary, fontFamily: fonts.medium }]}>
              {t('labels.addWorkspace')}
            </Text>
          </Pressable>

          <View style={[styles.popoutDivider, { backgroundColor: palette.divider }]} />

          <Pressable
            onPress={() => {
              setShowWorkspacePopout(false);
              openSettingsRoute();
            }}
            style={({ pressed }) => [
              styles.popoutItem,
              pressed && { backgroundColor: palette.listPressedBg },
            ]}
          >
            <View style={[styles.popoutBadge, { backgroundColor: palette.utilitySurface }]}>
              <Settings size={15} color={palette.textSecondary} strokeWidth={2} />
            </View>
            <Text style={[styles.popoutLabel, { color: theme.text, fontFamily: fonts.medium }]}>
              {t('settings.title')}
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <View pointerEvents="none" style={styles.countBadge}>
      <Text style={styles.countBadgeText}>
        {count > 99 ? '99+' : count}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  drawer: {
    flex: 1,
    position: 'relative',
  },
  // Pinned over the scroll body. Opaque so the list scrolls out of sight
  // behind it rather than showing through.
  headerBlock: {
    position: 'absolute',
    top: 0,
    start: 0,
    end: 0,
    paddingHorizontal: DRAWER_VISUAL_SPEC.horizontalPadding,
    paddingBottom: DRAWER_VISUAL_SPEC.headerBottomGap,
  },
  // Plain `row`: Yoga mirrors it under RTL, so the wordmark stays on the
  // leading side and the search circle on the trailing side in both directions.
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: DRAWER_VISUAL_SPEC.headerGap,
    height: DRAWER_VISUAL_SPEC.headerControlSize,
  },
  wordmarkPress: {
    paddingStart: 6,
    height: DRAWER_VISUAL_SPEC.headerControlSize,
    justifyContent: 'center',
  },
  wordmark: {
    width: DRAWER_VISUAL_SPEC.wordmarkWidth,
    height: DRAWER_VISUAL_SPEC.wordmarkHeight,
  },
  headerCircle: {
    width: DRAWER_VISUAL_SPEC.headerControlSize,
    height: DRAWER_VISUAL_SPEC.headerControlSize,
    borderRadius: DRAWER_VISUAL_SPEC.headerControlRadius,
    overflow: 'hidden',
  },
  headerCirclePress: {
    width: DRAWER_VISUAL_SPEC.headerControlSize,
    height: DRAWER_VISUAL_SPEC.headerControlSize,
    borderRadius: DRAWER_VISUAL_SPEC.headerControlRadius,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchInlineRow: {
    marginTop: 10,
    borderRadius: 14,
    minHeight: DRAWER_VISUAL_SPEC.searchInlineHeight,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    paddingVertical: 0,
  },
  searchClose: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The only in-flow child of the drawer, so it fills it from the origin.
  // See the `scrollBody` comment: that is deliberate, not incidental.
  scrollArea: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: DRAWER_VISUAL_SPEC.horizontalPadding,
  },
  navRow: {
    height: DRAWER_VISUAL_SPEC.navRowHeight,
    borderRadius: DRAWER_VISUAL_SPEC.navRowRadius,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    gap: 14,
  },
  navRowIcon: {
    width: DRAWER_VISUAL_SPEC.navIconSize,
    height: DRAWER_VISUAL_SPEC.navIconSize,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navRowLabel: {
    flex: 1,
    fontSize: DRAWER_VISUAL_SPEC.navLabelSize,
    fontWeight: '500',
    letterSpacing: -0.15,
    textAlign: 'left',
  },
  navRowSoon: {
    fontSize: 13,
    fontWeight: '500',
    paddingStart: 8,
  },
  neutralCountBadge: {
    minWidth: 22,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  neutralCountBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  countBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 5,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF3B30',
  },
  countBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    // Google Sans always loaded regardless of locale. Digit-only badges
    // must not use Cairo: its Latin metrics shift digits below centre in the pill.
    fontFamily: 'GoogleSans_700Bold',
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  categorySection: {
    marginTop: 10,
    marginBottom: 6,
  },
  categoryHeader: {
    height: 32,
    borderRadius: DRAWER_VISUAL_SPEC.threadRowRadius,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    marginTop: 6,
    gap: 6,
  },
  categoryTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: -0.1,
  },
  categoryHeaderAction: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryBody: {
    marginTop: 2,
    marginBottom: 8,
  },
  threadGroupHeading: {
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 0.1,
    paddingHorizontal: 10,
    paddingTop: 12,
    paddingBottom: 4,
  },
  threadItem: {
    height: DRAWER_VISUAL_SPEC.threadRowHeight,
    borderRadius: DRAWER_VISUAL_SPEC.threadRowRadius,
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
  },
  threadItemDeleting: {
    opacity: 0.55,
  },
  threadTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
    letterSpacing: -0.1,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 14,
    paddingVertical: 24,
  },
  // Pinned under the scroll body's end. Opaque for the same reason as the header.
  footerBlock: {
    position: 'absolute',
    bottom: 0,
    start: 0,
    end: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: DRAWER_VISUAL_SPEC.headerGap,
    paddingHorizontal: DRAWER_VISUAL_SPEC.horizontalPadding,
    paddingTop: DRAWER_VISUAL_SPEC.footerPaddingTop,
  },
  chatPill: {
    height: DRAWER_VISUAL_SPEC.chatFabHeight,
    borderRadius: DRAWER_VISUAL_SPEC.chatFabRadius,
    paddingHorizontal: DRAWER_VISUAL_SPEC.chatFabHorizontalPadding,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    ...shadows.md,
  },
  chatPillLabel: {
    color: '#FFFFFF',
    fontSize: DRAWER_VISUAL_SPEC.chatFabLabelSize,
    fontWeight: '600',
    letterSpacing: -0.15,
  },
  footerCircle: {
    width: DRAWER_VISUAL_SPEC.footerControlSize,
    height: DRAWER_VISUAL_SPEC.footerControlSize,
    borderRadius: DRAWER_VISUAL_SPEC.footerControlSize / 2,
    overflow: 'hidden',
  },
  footerMenu: { width: 72, flexDirection: 'row', gap: 6 },
  footerCirclePress: {
    width: DRAWER_VISUAL_SPEC.footerControlSize,
    height: DRAWER_VISUAL_SPEC.footerControlSize,
    borderRadius: DRAWER_VISUAL_SPEC.footerControlSize / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  popoutBackdrop: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 20,
  },
  popout: {
    position: 'absolute',
    end: DRAWER_VISUAL_SPEC.horizontalPadding,
    width: 274,
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    paddingVertical: 10,
    zIndex: 30,
    ...shadows.lg,
  },
  popoutTitle: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    paddingHorizontal: 14,
    marginBottom: 6,
  },
  popoutItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  popoutBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popoutBadgeIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  popoutDivider: {
    height: StyleSheet.hairlineWidth,
    marginHorizontal: 12,
    marginVertical: 6,
  },
  popoutLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
  },
  surfaceShadow: {
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 2,
  },
});
